import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { ScanFace, X, Copy, Check, ExternalLink } from "lucide-react";
import { useI18n } from "i18n";
import api from "lib/api";

// Links (or re-links) a face to a login account, which can then sign in with it at
// /liveness/login. Opens a modal with a QR + link to the hosted enrollment capture.
// Default: the CURRENT user (header entry point). Pass `targetUserId` (super admin) to
// generate a link for ANOTHER account — used in the account edit screen.
export default function FaceEnrollButton({ targetUserId = null }) {
  const { t } = useI18n();
  const [enrolled, setEnrolled] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);   // {enrollment_url, qr}
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  const loadStatus = useCallback(() => {
    const q = targetUserId ? `?user_id=${encodeURIComponent(targetUserId)}` : "";
    api.get(`/api/auth/face/status/${q}`)
      .then((r) => setEnrolled(!!r.data?.enrolled))
      .catch(() => setEnrolled(null));
  }, [targetUserId]);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const start = async () => {
    setOpen(true); setBusy(true); setErr(""); setResult(null);
    try {
      const r = await api.post("/api/auth/face/enroll-link/", targetUserId ? { user_id: targetUserId } : {});
      setResult(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || t("face.enroll.failed", "Falha ao gerar o link de cadastro."));
    } finally { setBusy(false); }
  };
  const copy = () => {
    navigator.clipboard?.writeText(result.enrollment_url);
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };
  const close = () => { setOpen(false); setResult(null); setErr(""); loadStatus(); };

  return (
    <>
      <button
        type="button"
        onClick={start}
        title={t("face.enroll.title", "Biometria facial")}
        className={targetUserId
          ? "inline-flex items-center gap-2 h-10 px-3.5 rounded-lg border border-border text-sm hover:bg-muted transition-colors"
          : "flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"}
      >
        <ScanFace size={16} className={enrolled ? "text-emerald-500" : ""} />
        <span className={targetUserId ? "" : "hidden sm:inline"}>
          {targetUserId
            ? (enrolled ? t("face.enroll.regenerate", "Atualizar cadastro facial") : t("face.enroll.generate", "Gerar link de cadastro facial"))
            : (enrolled ? t("face.enroll.manage", "Meu rosto") : t("face.enroll.cta", "Cadastrar meu rosto"))}
        </span>
        {enrolled && <Check size={13} className="text-emerald-500" />}
      </button>

      {open && createPortal(
        <div className="fixed inset-0 z-[120] bg-black/70 flex items-center justify-center p-4">
          <div className="w-full max-w-md rounded-2xl bg-card border border-border shadow-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <ScanFace className="h-5 w-5 text-primary" />
                <h2 className="font-semibold">{t("face.enroll.title", "Biometria facial")}</h2>
              </div>
              <button onClick={close} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
            </div>

            {busy && <p className="text-sm text-muted-foreground py-6 text-center">{t("face.enroll.generating", "Gerando link…")}</p>}
            {err && <p className="text-sm text-red-500">{err}</p>}

            {result && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  {enrolled
                    ? t("face.enroll.reHint", "Abra o link para atualizar o cadastro do seu rosto. Depois você poderá entrar pelo rosto na tela de login.")
                    : t("face.enroll.hint", "Abra o link (ou aponte a câmera para o QR) para cadastrar seu rosto. Depois você poderá entrar pelo rosto na tela de login.")}
                </p>
                {result.qr && (
                  <div className="flex justify-center">
                    <img src={result.qr} alt="QR" className="w-40 h-40 rounded-lg border border-border bg-white p-2" />
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <input readOnly value={result.enrollment_url}
                    className="flex-1 h-10 rounded-lg border border-border bg-muted text-sm px-3 font-mono" />
                  <button onClick={copy}
                    className="inline-flex items-center gap-1 h-10 px-3 rounded-lg border border-border text-sm hover:bg-muted">
                    {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <a href={result.enrollment_url} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90">
                    <ExternalLink className="h-4 w-4" /> {t("face.enroll.open", "Abrir cadastro")}
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body)}
    </>
  );
}
